import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { getImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Heart, MessageCircle, Send, Image as ImageIcon, MapPin, Briefcase, Crown, Eye, Building2, Zap, Users as UsersIcon, Calendar as CalendarIcon, UserPlus, ArrowRight, Youtube, X, Play } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import ImageLightbox from "@/components/ImageLightbox";

/* ---- Profile Sidebar (LinkedIn-style, sticky as one block) ---- */
function ProfileSidebar({ user, proEnabled }) {
  const initials = user?.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  const avatarUrl = getImageUrl(user?.profile_image);
  const [team, setTeam] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/teams/mine");
        if (active) setTeam(data && data.id ? data : null);
      } catch (e) {
        // no team or auth issue - silently leave team null
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <aside className="hidden lg:block w-64 shrink-0" data-testid="feed-sidebar">
      <div className="sticky top-20 space-y-3">
        {/* Profile card */}
        <div className="bg-white border border-[#E6E6E6] rounded-lg overflow-hidden">
          <div className="h-16 bg-gradient-to-r from-[#074572] to-[#0a5a8f]" />
          <div className="px-4 -mt-8">
            <Link to="/profile">
              <Avatar className="h-16 w-16 border-4 border-white shadow-sm">
                {avatarUrl ? <AvatarImage src={avatarUrl} /> : null}
                <AvatarFallback className="bg-[#074572] text-white text-lg font-semibold">{initials}</AvatarFallback>
              </Avatar>
            </Link>
          </div>
          <div className="px-4 pt-2 pb-4">
            <div className="flex items-center gap-2">
              <Link to="/profile" className="font-semibold text-sm text-zinc-900 hover:text-[#074572]" style={{ fontFamily: 'Outfit' }}>{user?.name}</Link>
              {user?.is_premium && <Crown size={12} className="text-amber-500" />}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="secondary" className="text-[10px] bg-[#074572]/5 text-[#074572] capitalize">{user?.role}</Badge>
              {user?.open_to_opportunities && (
                <Badge className="text-[10px] bg-green-50 text-green-700 border-green-200">
                  <Eye size={8} className="mr-1" /> Open
                </Badge>
              )}
            </div>
            {user?.position && <p className="text-xs text-zinc-500 mt-1">{user.position}</p>}
            <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
              {user?.location && <span className="flex items-center gap-1"><MapPin size={10} />{user.location}</span>}
              {user?.club && <span className="flex items-center gap-1"><Briefcase size={10} />{user.club}</span>}
            </div>
          </div>
          <Separator className="bg-[#E6E6E6]" />
          <div className="text-center py-3">
            <p className="text-sm font-semibold text-[#074572]">{user?.connection_count || 0}</p>
            <p className="text-[10px] text-zinc-400">Connections</p>
          </div>
          {proEnabled && !user?.is_premium && (
            <>
              <Separator className="bg-[#E6E6E6]" />
              <Link to="/premium" className="flex items-center gap-2 px-4 py-3 text-xs text-[#F85037] font-medium hover:bg-[#F85037]/5 transition-colors" data-testid="sidebar-premium-link">
                <Crown size={14} /> Upgrade to Premium
              </Link>
            </>
          )}
        </div>

        {/* Team card (LinkedIn-style company under profile) */}
        {team ? (
          <Link to={`/teams/${team.id}`} className="block bg-white border border-[#E6E6E6] rounded-lg p-3 hover:border-[#074572]/30 hover:shadow-sm transition-all" data-testid="sidebar-team-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#074572]/5 flex items-center justify-center shrink-0 overflow-hidden">
                {team.logo_url ? <img src={team.logo_url} alt={team.name} className="w-full h-full object-cover" /> : <Building2 size={18} className="text-[#074572]" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-900 truncate">{team.name}</p>
                <p className="text-[11px] text-zinc-500 capitalize truncate">{team.team_type}{team.location ? ` · ${team.location}` : ""}</p>
              </div>
            </div>
          </Link>
        ) : (
          <Link to="/team" className="block bg-white border border-dashed border-[#E6E6E6] rounded-lg p-3 hover:border-[#074572]/40 transition-colors" data-testid="sidebar-create-team">
            <div className="flex items-center gap-3 text-zinc-500">
              <div className="w-10 h-10 rounded-md bg-zinc-50 flex items-center justify-center shrink-0">
                <Building2 size={18} className="text-zinc-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-zinc-700">Create your Team</p>
                <p className="text-[11px] text-zinc-400">Show your club or academy on your profile</p>
              </div>
            </div>
          </Link>
        )}

        {/* Secondary nav */}
        <div className="bg-white border border-[#E6E6E6] rounded-lg overflow-hidden" data-testid="sidebar-secondary-nav">
          <SidebarLink to="/spotlight" icon={Zap} label="Spotlight" desc="Featured talent" />
          <Separator className="bg-[#E6E6E6]" />
          <SidebarLink to="/connections" icon={UsersIcon} label="Network" desc="Connections & requests" />
          <Separator className="bg-[#E6E6E6]" />
          <SidebarLink to="/events" icon={CalendarIcon} label="Events" desc="Discover & create" />
        </div>
      </div>
    </aside>
  );
}

function SidebarLink({ to, icon: Icon, label, desc }) {
  return (
    <Link to={to} className="flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50 transition-colors" data-testid={`sidebar-link-${label.toLowerCase()}`}>
      <Icon size={16} className="text-[#074572] shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-zinc-900">{label}</p>
        <p className="text-[11px] text-zinc-400">{desc}</p>
      </div>
    </Link>
  );
}

/* ---- Right Rail: Recommendations, Upcoming Events, Ads ---- */
function RightRail() {
  const [recs, setRecs] = useState([]);
  const [events, setEvents] = useState([]);
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [recRes, evRes, adRes] = await Promise.all([
          api.get("/scout/recommendations").catch(() => ({ data: [] })),
          api.get("/events?limit=3").catch(() => ({ data: { events: [] } })),
          api.get("/ads/random").catch(() => ({ data: null })),
        ]);
        if (!active) return;
        setRecs((recRes.data || []).slice(0, 3));
        setEvents((evRes.data?.events || []).slice(0, 3));
        setAd(adRes.data || null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleConnect = async (id) => {
    try {
      await api.post(`/connections/request/${id}`);
      toast.success("Connection request sent");
      setRecs(prev => prev.filter(r => (r._id || r.id) !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to connect");
    }
  };

  const handleAdClick = async (e) => {
    if (!ad?.id) return;
    // Fire-and-forget click tracking
    api.post(`/ads/${ad.id}/click`).catch(() => {});
    // Let the anchor navigate normally (target=_blank)
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return ""; }
  };

  return (
    <aside className="hidden xl:block w-72 shrink-0" data-testid="feed-right-rail">
      <div className="sticky top-20 space-y-3">
        {/* Recommendations */}
        <div className="bg-white border border-[#E6E6E6] rounded-lg overflow-hidden" data-testid="right-rail-recommendations">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E6E6]">
            <div className="flex items-center gap-2">
              <UsersIcon size={14} className="text-[#074572]" />
              <h3 className="text-sm font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>Recommendations</h3>
            </div>
          </div>
          {loading ? (
            <div className="p-4 text-xs text-zinc-400 text-center">Loading...</div>
          ) : recs.length === 0 ? (
            <div className="p-4 text-xs text-zinc-400 text-center">No suggestions yet</div>
          ) : (
            <div className="divide-y divide-[#E6E6E6]">
              {recs.map(r => {
                const rid = r._id || r.id;
                const rInitials = r.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
                const rAvatar = getImageUrl(r.profile_image);
                return (
                  <div key={rid} className="flex items-center gap-2 px-3 py-2.5" data-testid={`rail-rec-${rid}`}>
                    <Link to={`/profile/${rid}`} className="shrink-0">
                      <Avatar className="h-9 w-9 border border-[#E6E6E6]">
                        {rAvatar ? <AvatarImage src={rAvatar} /> : null}
                        <AvatarFallback className="bg-[#074572] text-white text-[10px]">{rInitials}</AvatarFallback>
                      </Avatar>
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/profile/${rid}`} className="text-xs font-medium text-zinc-900 hover:text-[#074572] truncate block">{r.name}</Link>
                      <p className="text-[10px] text-zinc-400 capitalize truncate">
                        {r.role}{r.position ? ` · ${r.position}` : ""}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleConnect(rid)}
                      className="h-7 px-2 text-[#F85037] hover:bg-[#F85037]/5"
                      data-testid={`rail-connect-${rid}`}
                    >
                      <UserPlus size={13} />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          <Link
            to="/connections"
            className="flex items-center justify-between px-4 py-2.5 text-[11px] font-medium text-[#074572] bg-[#074572]/5 hover:bg-[#074572]/10 transition-colors"
            data-testid="rail-view-all-recs"
          >
            <span>See all</span>
            <ArrowRight size={11} />
          </Link>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white border border-[#E6E6E6] rounded-lg overflow-hidden" data-testid="right-rail-events">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E6E6]">
            <div className="flex items-center gap-2">
              <CalendarIcon size={14} className="text-[#074572]" />
              <h3 className="text-sm font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>Upcoming Events</h3>
            </div>
          </div>
          {loading ? (
            <div className="p-4 text-xs text-zinc-400 text-center">Loading...</div>
          ) : events.length === 0 ? (
            <div className="p-4 text-xs text-zinc-400 text-center">No events scheduled</div>
          ) : (
            <div className="divide-y divide-[#E6E6E6]">
              {events.map(ev => (
                <Link
                  key={ev.id}
                  to={`/events/${ev.id}`}
                  className="flex gap-3 px-3 py-2.5 hover:bg-zinc-50 transition-colors"
                  data-testid={`rail-event-${ev.id}`}
                >
                  <div className="w-10 h-10 rounded-md bg-[#F85037]/10 flex flex-col items-center justify-center shrink-0">
                    <span className="text-[9px] text-[#F85037] font-semibold uppercase">
                      {new Date(ev.date).toLocaleDateString(undefined, { month: "short" })}
                    </span>
                    <span className="text-sm font-bold text-[#F85037] leading-none">
                      {new Date(ev.date).getDate()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-zinc-900 truncate">{ev.title}</p>
                    <p className="text-[10px] text-zinc-400 truncate">
                      {formatDate(ev.date)}{ev.location ? ` · ${ev.location}` : ""}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
          <Link
            to="/events"
            className="flex items-center justify-between px-4 py-2.5 text-[11px] font-medium text-[#074572] bg-[#074572]/5 hover:bg-[#074572]/10 transition-colors"
            data-testid="rail-view-all-events"
          >
            <span>See all events</span>
            <ArrowRight size={11} />
          </Link>
        </div>

        {/* Ad Slot */}
        {ad ? (
          <a
            href={ad.target_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={handleAdClick}
            className="block bg-white border border-[#E6E6E6] rounded-lg overflow-hidden hover:shadow-md hover:border-[#074572]/30 transition-all"
            data-testid="right-rail-ad"
          >
            <div className="px-4 py-2 border-b border-[#E6E6E6] bg-zinc-50/70 flex items-center justify-between">
              <span className="text-[9px] font-semibold text-zinc-400 uppercase tracking-wider">Sponsored by {ad.sponsor_name}</span>
            </div>
            {ad.image_url && (
              <img
                src={ad.image_url.startsWith("http") ? ad.image_url : getImageUrl(ad.image_url)}
                alt={ad.headline}
                className="w-full aspect-video object-cover"
              />
            )}
            <div className="p-3">
              <p className="text-sm font-semibold text-zinc-900 line-clamp-2" style={{ fontFamily: 'Outfit' }}>{ad.headline}</p>
              {ad.description && (
                <p className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{ad.description}</p>
              )}
              <div className="flex items-center gap-1 text-[11px] font-medium text-[#F85037] mt-2">
                Learn more <ArrowRight size={11} />
              </div>
            </div>
          </a>
        ) : (
          <div
            className="bg-gradient-to-br from-zinc-50 to-white border border-dashed border-[#E6E6E6] rounded-lg overflow-hidden"
            data-testid="right-rail-ads-placeholder"
          >
            <div className="px-4 py-2 border-b border-[#E6E6E6] bg-white/50">
              <span className="text-[9px] font-semibold text-zinc-400 uppercase tracking-wider">Sponsored</span>
            </div>
            <div className="p-6 flex flex-col items-center justify-center text-center min-h-[180px]">
              <div className="w-12 h-12 rounded-full bg-[#074572]/5 flex items-center justify-center mb-3">
                <Zap size={20} className="text-[#074572]/40" />
              </div>
              <p className="text-xs font-medium text-zinc-500">Your ad here</p>
              <p className="text-[10px] text-zinc-400 mt-1">Reach thousands of scouts, coaches and athletes</p>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

/* ---- Post Composer ---- */
function PostComposer({ onPost }) {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [showYoutube, setShowYoutube] = useState(false);
  const { user } = useAuth();

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) { setImageFile(file); setImagePreview(URL.createObjectURL(file)); setYoutubeUrl(""); setShowYoutube(false); }
  };
  const removeImage = () => { setImageFile(null); if (imagePreview) URL.revokeObjectURL(imagePreview); setImagePreview(null); };
  const toggleYoutube = () => { setShowYoutube((s) => !s); if (imageFile) removeImage(); };

  const handlePost = async () => {
    if (!content.trim()) return;
    setLoading(true);
    try {
      let data;
      if (youtubeUrl.trim()) {
        const res = await api.post("/posts", { content, youtube_url: youtubeUrl.trim() });
        data = res.data;
      } else if (imageFile) {
        const formData = new FormData();
        formData.append("content", content);
        formData.append("image", imageFile);
        const res = await api.post("/posts/with-image", formData, { headers: { "Content-Type": "multipart/form-data" } });
        data = res.data;
      } else {
        const res = await api.post("/posts", { content });
        data = res.data;
      }
      onPost(data);
      setContent("");
      removeImage();
      setYoutubeUrl("");
      setShowYoutube(false);
      toast.success("Post published!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create post");
    }
    finally { setLoading(false); }
  };

  const initials = user?.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  const avatarUrl = getImageUrl(user?.profile_image);

  return (
    <div className="bg-white border border-[#E6E6E6] rounded-lg p-5 mb-4" data-testid="post-composer">
      <div className="flex gap-3">
        <Avatar className="h-10 w-10 border-2 border-[#E6E6E6] shrink-0">
          {avatarUrl ? <AvatarImage src={avatarUrl} /> : null}
          <AvatarFallback className="bg-[#074572] text-white text-sm">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <Textarea
            placeholder="Share an update with your network..."
            value={content} onChange={(e) => setContent(e.target.value)}
            className="border-[#E6E6E6] resize-none min-h-[80px] focus:ring-[#074572]/20 focus:border-[#074572]"
            data-testid="post-content-input"
          />
          {imagePreview && (
            <div className="relative mt-2 inline-block">
              <img src={imagePreview} alt="Preview" className="max-h-40 rounded-md border border-[#E6E6E6]" />
              <button onClick={removeImage} className="absolute -top-2 -right-2 w-5 h-5 bg-zinc-900 text-white rounded-full flex items-center justify-center text-xs hover:bg-[#F85037]" data-testid="remove-image-btn">x</button>
            </div>
          )}
          {showYoutube && (
            <div className="relative mt-2 flex items-center gap-2">
              <Youtube size={18} className="text-[#FF0000] shrink-0" />
              <input
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="Paste a YouTube link (youtube.com/watch?v=… or youtu.be/…)"
                className="flex-1 text-sm border border-[#E6E6E6] rounded-md px-3 py-1.5 focus:outline-none focus:border-[#074572]"
                data-testid="post-youtube-input"
              />
              <button onClick={() => { setYoutubeUrl(""); setShowYoutube(false); }} className="text-zinc-400 hover:text-[#F85037]" data-testid="post-youtube-clear"><X size={16} /></button>
            </div>
          )}
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-1">
              <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" id="post-image-input" disabled={!!youtubeUrl.trim()} />
              <label htmlFor="post-image-input" className={youtubeUrl.trim() ? "opacity-40 pointer-events-none" : "cursor-pointer"}>
                <Button variant="ghost" size="sm" className="text-zinc-400 gap-2" asChild><span><ImageIcon size={16} /> Photo</span></Button>
              </label>
              <Button variant="ghost" size="sm" onClick={toggleYoutube} className={`gap-2 ${showYoutube ? "text-[#FF0000]" : "text-zinc-400"}`} data-testid="post-youtube-toggle" disabled={!!imageFile}>
                <Youtube size={16} /> YouTube
              </Button>
            </div>
            <Button onClick={handlePost} disabled={!content.trim() || loading} className="bg-[#F85037] hover:bg-[#e04530] text-white gap-2" data-testid="post-submit-btn">
              <Send size={14} /> {loading ? "Posting..." : "Post"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- Post Item ---- */
function PostItem({ post, currentUserId }) {
  const [liked, setLiked] = useState(post.likes?.includes(currentUserId));
  const [likeCount, setLikeCount] = useState(post.likes?.length || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(post.comments || []);
  const [commentText, setCommentText] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  const handleLike = async () => {
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setLiked(data.liked);
      setLikeCount(data.like_count);
    } catch { toast.error("Failed to like post"); }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    try {
      const { data } = await api.post(`/posts/${post.id}/comment`, { content: commentText });
      setComments([...comments, data]);
      setCommentText("");
    } catch { toast.error("Failed to add comment"); }
  };

  const initials = post.author_name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
  const authorAvatar = getImageUrl(post.author_image);
  const postImage = post.image_url ? (post.image_url.startsWith("http") ? post.image_url : getImageUrl(post.image_url)) : null;
  const timeAgo = getTimeAgo(post.created_at);

  // Parse hashtags
  const renderContent = (text) => {
    return text.split(/(#\w+)/g).map((part, i) =>
      part.startsWith("#") ? <span key={i} className="text-[#074572] font-medium cursor-pointer hover:underline">{part}</span> : part
    );
  };

  return (
    <div className="bg-white border border-[#E6E6E6] rounded-lg py-5 px-5 mb-3" data-testid={`post-${post.id}`}>
      <div className="flex items-start gap-3">
        <button onClick={() => authorAvatar && setLightbox(authorAvatar)} data-testid={`post-avatar-${post.id}`} className={authorAvatar ? "cursor-pointer" : "cursor-default"}>
          <Avatar className="h-10 w-10 border-2 border-[#E6E6E6]">
            {authorAvatar ? <AvatarImage src={authorAvatar} /> : null}
            <AvatarFallback className="bg-[#074572] text-white text-sm">{initials}</AvatarFallback>
          </Avatar>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/profile/${post.author_id}`} className="font-medium text-zinc-900 hover:text-[#074572] text-sm">{post.author_name}</Link>
            <Badge variant="secondary" className="text-[10px] bg-[#074572]/5 text-[#074572] capitalize">{post.author_role}</Badge>
            <span className="text-xs text-zinc-400">{timeAgo}</span>
          </div>
          <p className="text-sm text-zinc-700 mt-2 leading-relaxed whitespace-pre-wrap">{renderContent(post.content)}</p>
          {postImage && (
            <img src={postImage} alt="" onClick={() => setLightbox(postImage)} className="mt-3 rounded-md w-full max-h-80 object-cover border border-[#E6E6E6] cursor-pointer" data-testid={`post-image-${post.id}`} />
          )}
          {post.youtube_id && (
            <div className="mt-3 relative w-full rounded-md overflow-hidden border border-[#E6E6E6] bg-black" style={{ aspectRatio: "16 / 9" }} data-testid={`post-youtube-${post.id}`}>
              {videoPlaying ? (
                <iframe
                  src={`https://www.youtube.com/embed/${post.youtube_id}?autoplay=1`}
                  title="YouTube video"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <button onClick={() => setVideoPlaying(true)} className="absolute inset-0 w-full h-full group" data-testid={`post-youtube-play-${post.id}`} aria-label="Play video">
                  <img src={`https://img.youtube.com/vi/${post.youtube_id}/hqdefault.jpg`} alt="Video thumbnail" className="w-full h-full object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                    <span className="w-16 h-16 rounded-full bg-[#FF0000] flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                      <Play fill="white" className="text-white ml-1" size={26} />
                    </span>
                  </span>
                </button>
              )}
            </div>
          )}
          <div className="flex items-center gap-4 mt-3">
            <button onClick={handleLike} className={`flex items-center gap-1.5 text-sm transition-colors ${liked ? "text-[#F85037]" : "text-zinc-400 hover:text-[#F85037]"}`} data-testid={`post-like-${post.id}`}>
              <Heart size={16} fill={liked ? "#F85037" : "none"} /> {likeCount}
            </button>
            <button onClick={() => setShowComments(!showComments)} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-[#074572] transition-colors" data-testid={`post-comment-toggle-${post.id}`}>
              <MessageCircle size={16} /> {comments.length}
            </button>
          </div>
          {showComments && (
            <div className="mt-4 space-y-3" data-testid={`post-comments-${post.id}`}>
              {comments.map(c => (
                <div key={c.id} className="flex gap-2 text-sm">
                  <span className="font-medium text-zinc-800">{c.author_name}</span>
                  <span className="text-zinc-600">{c.content}</span>
                </div>
              ))}
              <div className="flex gap-2">
                <input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Write a comment..."
                  className="flex-1 text-sm border border-[#E6E6E6] rounded-md px-3 py-1.5 focus:outline-none focus:border-[#074572]"
                  onKeyDown={(e) => e.key === "Enter" && handleComment()} data-testid={`comment-input-${post.id}`} />
                <Button size="sm" onClick={handleComment} className="bg-[#074572] text-white hover:bg-[#053a60]" data-testid={`comment-submit-${post.id}`}>Reply</Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <ImageLightbox src={lightbox} alt={post.author_name} onClose={() => setLightbox(null)} />
    </div>
  );
}

function getTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/* ---- Main Feed Page ---- */
export default function FeedPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [proEnabled, setProEnabled] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postsRes, profileRes, settingsRes] = await Promise.all([
          api.get("/posts"),
          api.get(`/users/${user?._id || user?.id}`),
          api.get("/platform/settings"),
        ]);
        setPosts(postsRes.data.posts || []);
        setProfileData(profileRes.data);
        setProEnabled(settingsRes.data.pro_enabled || false);
      } catch { toast.error("Failed to load feed"); }
      finally { setLoading(false); }
    };
    if (user) fetchData();
  }, [user]);

  const handleNewPost = (post) => setPosts([post, ...posts]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex gap-6" data-testid="feed-page">
      {/* Left Sidebar */}
      <ProfileSidebar user={profileData || user} proEnabled={proEnabled} />

      {/* Main Feed */}
      <div className="flex-1 min-w-0 max-w-2xl mx-auto">
        {user?.role !== "parent" && <PostComposer onPost={handleNewPost} />}

        {loading ? (
          <div className="p-12 text-center text-zinc-400">Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className="p-12 text-center text-zinc-400">No posts yet. Be the first to share!</div>
        ) : (
          posts.map(post => <PostItem key={post.id} post={post} currentUserId={user?._id || user?.id} />)
        )}
      </div>

      {/* Right Rail */}
      <RightRail />
    </div>
  );
}
